import { EyeIcon, FolderIcon, PlusIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Stack, Text } from '@prophecy/ui-v3';
import React, { useState } from 'react';

import { PsetContent } from '../../components/PsetContent';
import { StyledCenteredCard, StyledInput, StyledMinusIcon, StyledStackValueContainer, StyledText } from '../styled';
import { ActionTypes, FileTypes } from '../types';
import { TranspilerFileBrowser } from './TranspilerFileBrowser';

type HeaderType = {
  heading: string;
  btnText?: string;
  onChange: (value: string, index: number, action?: ActionTypes) => void;
};
export const OptionsHeader = ({ heading, btnText = 'Add Psets', onChange }: HeaderType) => {
  return (
    <Stack direction='horizontal' align='space-between' width='100%' alignY='center'>
      <Text level='sm'>{heading}</Text>
      <Button
        size='s'
        onClick={() => onChange('', 0, ActionTypes.add)}
        iconPlacement='left'
        variant='secondaryGray'
        icon={<PlusIcon type='default' />}
        data-test-id='transpiler-options-add-button'>
        {btnText}
      </Button>
    </Stack>
  );
};
export const TranspilerOptions = ({
  btnText,
  heading,
  parentPsetPath,
  hasPath,
  values,
  onChange,
  hasHeader = true
}: HeaderType & {
  values: string[];
  parentPsetPath: string;
  hasPath?: boolean;
  hasHeader?: boolean;
}) => {
  const [currentIndex, setCurrentIndex] = useState(-1);

  const [psetContentIndex, setPsetContentIndex] = useState(-1);

  return (
    <Stack gap={theme.spaces.x16} width='100%' height='100%'>
      {hasHeader && <OptionsHeader onChange={onChange} heading={heading} btnText={btnText} />}
      <StyledCenteredCard background={theme.colors.gray50}>
        {values.length < 1 && <StyledText level='sm'>No record.</StyledText>}
        <Stack gap={theme.spaces.x12} width='100%'>
          {values.map((value, i) => {
            return (
              <StyledStackValueContainer key={i} direction='horizontal' gap={theme.spaces.x12} width='100%'>
                {currentIndex === i && (
                  <TranspilerFileBrowser
                    selectedPath={value}
                    psetList={values}
                    extension={FileTypes.pset}
                    parentPsetPath={parentPsetPath}
                    onChange={(value) => onChange(value, i)}
                    toggleFilePath={() => setCurrentIndex(-1)}
                  />
                )}
                {psetContentIndex === i && !hasPath && (
                  <PsetContent path={value} onClose={() => setPsetContentIndex(-1)} />
                )}
                <StyledInput
                  addOnPlacement='right'
                  value={value}
                  icon={hasPath || !value ? null : <EyeIcon type='default' onClick={() => setPsetContentIndex(i)} />}
                  addOn={
                    hasPath ? null : (
                      <Button
                        onClick={() => setCurrentIndex(i)}
                        variant='tertiaryGray'
                        size='s'
                        icon={<FolderIcon type='default' size='xs' />}
                        data-test-id='transpiler-options-folder-button'
                        aria-label='Browse folder'
                      />
                    )
                  }
                  onChange={(value) => onChange(value, i)}
                  data-test-id='transpiler-options-value-input'
                />
                <Button
                  onClick={() => onChange(value, i, ActionTypes.delete)}
                  variant='plain'
                  icon={<StyledMinusIcon type='solid' color={theme.colors.gray400} />}
                  data-test-id='transpiler-options-delete-button'
                  aria-label='Delete transpiler option'
                />
              </StyledStackValueContainer>
            );
          })}
        </Stack>
      </StyledCenteredCard>
    </Stack>
  );
};
